import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '../../../components/ui';
import { DynamicColumn, DynamicListTable } from '../../../components/DynamicListTable';
import { NumerosGrid, TipoNumerosGrid } from '../../../components/NumerosGrid';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { generarPdfLista, generarPdfListaBlob } from '../../../utils/pdfListas';
import { shareImageNative } from '../../../utils/nativeShare';
import { openWhatsApp } from '../../../utils/whatsappShare';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';

// ── Tipos ──────────────────────────────────────────────────

interface SorteoItem {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  estado: string;
  jugadas_hoy?: number;
}

interface PaqueteResumen {
  paquete_id: string;
  paquete_nombre: string;
  local_ids: string[];
}

interface AuditoriaConfig {
  tipo_id: string;
  commission_percentage: string | null;
  commission_exempt_amount: string | null;
  commission_max_amount: string | null;
  max_number_sales: number | null;
  limite_jugada: string | null;
  alcance_limite_jugada: string | null;
}

interface AuditoriaPremio {
  tipo_id: string;
  concepto: string;
  multiplicador: string;
}

interface AuditoriaRestringido {
  tipo_id: string;
  numeros: string[];
  auto_dia: boolean;
  max_number_sales: number | null;
  riesgo_pct: string | null;
  minimo_permitido: string | null;
}

interface AuditoriaBloqueado {
  tipo_id: string;
  numero: string;
}

interface AuditoriaData {
  paquete_id: string;
  paquete_nombre: string;
  locales: { local_id: string; local_nombre: string }[];
  configs: AuditoriaConfig[];
  premios: AuditoriaPremio[];
  restringidos: AuditoriaRestringido[];
  bloqueados_globales: AuditoriaBloqueado[];
}

// ── Helpers de formato (misma lógica que ListasPage) ───────

const monto = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapMontos = (rows: any[], keys: string[]): any[] =>
  rows.map((row, index) => {
    const out = { ...row, id: row.id ?? index + 1 };
    for (const key of keys) {
      if (key in out) out[key] = monto(out[key]);
    }
    return out;
  });

const columnsByTipo = (tipo: string): DynamicColumn[] => {
  switch (tipo) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '160px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '160px' },
      ];
    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '100px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '130px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '130px' },
        { key: 'num2', label: 'NÚM2', type: 'readonly', width: '80px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '140px' },
      ];
    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '120px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '100px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    default:
      return [];
  }
};

const montoKeysByTipo: Record<string, string[]> = {
  NORMAL: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  REVENTADO: ['vendido_rev1', 'vendido_rev2'],
  MEGA: ['rev1', 'rev2', 'monto_mega'],
  MONAZOS: ['vendido'],
  FECHAS: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  PARLEY: ['vendido'],
};

type Vista = 'general' | 'paquete';

// ── Componente de sección colapsable ───────────────────────

const SeccionColapsable: React.FC<{
  titulo: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ titulo, children, defaultOpen = true }) => {
  const [abierta, setAbierta] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setAbierta(!abierta)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        {abierta
          ? <ChevronDown size={16} className="text-gray-400" />
          : <ChevronRight size={16} className="text-gray-400" />}
        <span className="font-medium text-sm text-gray-700">{titulo}</span>
      </button>
      {abierta && <div className="p-4">{children}</div>}
    </div>
  );
};

// ── Componente principal ───────────────────────────────────

export const BanqueroListasPage: React.FC = () => {
  // Filtros
  const [fechaFiltro, setFechaFiltro] = useState(() => fechaLocalYMD());
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState('');
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [selectedPaqueteId, setSelectedPaqueteId] = useState('');
  const [vista, setVista] = useState<Vista>('general');

  // Datos
  const [rows, setRows] = useState<any[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [auditoria, setAuditoria] = useState<AuditoriaData | null>(null);
  const [loadingAuditoria, setLoadingAuditoria] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [enviandoWa, setEnviandoWa] = useState(false);

  const selectedSorteo = useMemo(
    () => sorteos.find((s) => s.id === selectedSorteoId) || null,
    [sorteos, selectedSorteoId],
  );

  // ── Carga inicial: sorteos + paquetes ──────────

  useEffect(() => {
    Promise.all([
      fetchAPI<SorteoItem[]>(`/sorteos?fecha=${fechaFiltro}`),
      fetchAPI<PaqueteResumen[]>('/paquetes-reglas/resumen-banquero'),
    ])
      .then(([sorteosData, paquetesData]) => {
        setSorteos(sorteosData);
        setPaquetes(paquetesData);
        setSelectedSorteoId((prev) => {
          if (!sorteosData.length) return '';
          if (sorteosData.find((s) => s.id === prev)) return prev;
          const conData = [...sorteosData].sort(
            (a, b) => (b.jugadas_hoy ?? 0) - (a.jugadas_hoy ?? 0),
          );
          return (conData[0]?.jugadas_hoy ?? 0) > 0 ? conData[0].id : sorteosData[0].id;
        });
      })
      .catch((err) => console.error('Error cargando datos iniciales:', err));
  }, [fechaFiltro]);

  // ── Carga de lista consolidada ───────────────────────────

  useEffect(() => {
    if (!selectedSorteoId) return;
    setLoadingRows(true);

    const tipo = selectedSorteo?.tipo_id || '';
    const params = new URLSearchParams();
    params.append('fecha', fechaFiltro);

    let basePath: string;

    if (vista === 'paquete' && selectedPaqueteId) {
      params.append('paquete_id', selectedPaqueteId);
      basePath = `/listas/consolidada-paquete/${selectedSorteoId}`;
    } else {
      basePath = tipo === 'MEGA'
        ? `/listas/individuales/${selectedSorteoId}`
        : `/listas/consolidada/${selectedSorteoId}`;
    }

    fetchAPI<any[]>(`${basePath}?${params.toString()}`)
      .then((data) => {
        // NumerosGrid para NORMAL espera { numero, vendido }
        // En vista general el endpoint original ya devuelve ese formato
        // En vista paquete el endpoint devuelve { numero, monto_total, ... } — normalizar igual
        if (tipo === 'NORMAL') {
          setRows((data || []).map((r: any) => ({
            ...r,
            numero: String(r.numero).padStart(2, '0'),
            vendido: Number(r.monto_total ?? r.vendido) || 0,
          })));
        } else {
          setRows(data || []);
        }
      })
      .catch((err) => {
        console.error('Error cargando consolidado:', err);
        setRows([]);
      })
      .finally(() => setLoadingRows(false));
  }, [selectedSorteoId, selectedSorteo?.tipo_id, fechaFiltro, vista, selectedPaqueteId, refreshTick]);

  // ── Carga de auditoría del paquete ───────────────────────

  useEffect(() => {
    if (vista !== 'paquete' || !selectedPaqueteId) {
      setAuditoria(null);
      return;
    }
    setLoadingAuditoria(true);
    fetchAPI<AuditoriaData>(`/paquetes-reglas/${selectedPaqueteId}/auditoria`)
      .then((data) => setAuditoria(data))
      .catch((err) => {
        console.error('Error cargando auditoría:', err);
        setAuditoria(null);
      })
      .finally(() => setLoadingAuditoria(false));
  }, [selectedPaqueteId, vista]);

  // ── Filas formateadas ────────────────────────────────────

  const formattedRows = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (tipo === 'REVENTADO') return rows;
    return mapMontos(rows, montoKeysByTipo[tipo] || ['vendido']);
  }, [rows, selectedSorteo?.tipo_id]);

  const totalesConsolidado = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (!['NORMAL', 'FECHAS'].includes(tipo) || rows.length === 0) return null;
    return {
      monto_vendedor: rows.reduce((s: number, r: any) => s + (r.monto_vendedor || 0), 0),
      monto_local: rows.reduce((s: number, r: any) => s + (r.monto_local || 0), 0),
      monto_banca: rows.reduce((s: number, r: any) => s + (r.monto_banca || 0), 0),
      monto_total: rows.reduce((s: number, r: any) => s + (r.monto_total || 0), 0),
    };
  }, [rows, selectedSorteo?.tipo_id]);

  const recargar = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  const descargarPdf = useCallback(async () => {
    if (!selectedSorteoId || !selectedSorteo) return;
    setGenerandoPdf(true);
    try {
      const tipo = selectedSorteo.tipo_id || 'NORMAL';
      const params = new URLSearchParams();
      params.append('fecha', fechaFiltro);

      let basePath: string;
      if (vista === 'paquete' && selectedPaqueteId) {
        params.append('paquete_id', selectedPaqueteId);
        basePath = `/listas/consolidada-paquete/${selectedSorteoId}`;
      } else {
        basePath = tipo === 'MEGA'
          ? `/listas/individuales/${selectedSorteoId}`
          : `/listas/consolidada/${selectedSorteoId}`;
      }

      const freshData = await fetchAPI<any[]>(`${basePath}?${params.toString()}`);
      let dataFinal = freshData || [];
      if (tipo === 'NORMAL') {
        dataFinal = dataFinal.map((r: any) => ({
          ...r,
          numero: String(r.numero).padStart(2, '0'),
          vendido: Number(r.monto_total ?? r.vendido) || 0,
        }));
      }

      const paq = paquetes.find(p => p.paquete_id === selectedPaqueteId);
      generarPdfLista({
        sorteo: selectedSorteo,
        fecha: fechaFiltro,
        paqueteNombre: vista === 'paquete' && paq ? paq.paquete_nombre : null,
        localNombre: null,
        tipo,
        rows: dataFinal,
      });
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF. Intente de nuevo.');
    } finally {
      setGenerandoPdf(false);
    }
  }, [selectedSorteoId, selectedSorteo, vista, selectedPaqueteId, fechaFiltro, paquetes]);

  const enviarWa = useCallback(async () => {
    if (!selectedSorteoId || !selectedSorteo || rows.length === 0 || enviandoWa) return;
    setEnviandoWa(true);
    try {
      const tipo = selectedSorteo.tipo_id || 'NORMAL';
      const params = new URLSearchParams();
      params.append('fecha', fechaFiltro);
      let basePath: string;
      if (vista === 'paquete' && selectedPaqueteId) {
        params.append('paquete_id', selectedPaqueteId);
        basePath = `/listas/consolidada-paquete/${selectedSorteoId}`;
      } else {
        basePath = tipo === 'MEGA'
          ? `/listas/individuales/${selectedSorteoId}`
          : `/listas/consolidada/${selectedSorteoId}`;
      }
      const freshData = await fetchAPI<any[]>(`${basePath}?${params.toString()}`);
      let dataFinal = freshData || [];
      if (tipo === 'NORMAL') {
        dataFinal = dataFinal.map((r: any) => ({
          ...r,
          numero: String(r.numero).padStart(2, '0'),
          vendido: Number(r.monto_total ?? r.vendido) || 0,
        }));
      }
      const paq = paquetes.find(p => p.paquete_id === selectedPaqueteId);
      const paqNombre = paq ? paq.paquete_nombre : 'General';
      const blob = await generarPdfListaBlob({
        sorteo: selectedSorteo,
        fecha: fechaFiltro,
        paqueteNombre: vista === 'paquete' && paq ? paq.paquete_nombre : null,
        localNombre: null,
        tipo,
        rows: dataFinal,
      });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const safeName = selectedSorteo.nombre.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `lista-${safeName}-${fechaFiltro}-${ts}.pdf`;
      const texto = `Lista del sorteo ${selectedSorteo.nombre} - ${fechaFiltro} - Paquete: ${paqNombre}`;
      const shared = await shareImageNative(blob, fileName, texto);
      if (!shared) {
        openWhatsApp(texto);
      }
    } catch (err) {
      console.error('Error enviando por WhatsApp:', err);
    } finally {
      setEnviandoWa(false);
    }
  }, [selectedSorteoId, selectedSorteo, vista, selectedPaqueteId, fechaFiltro, paquetes, rows, enviandoWa]);

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listas por Paquete</h1>
          <p className="text-sm text-gray-500 mt-1">
            Consulta de ventas por sorteo, paquete de reglas y local
          </p>
        </div>
        <button
          onClick={recargar}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Actualizar
        </button>
      </div>

      {/* ZONA 1 — Filtros */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Fecha */}
          <div>
            <label className="text-sm text-gray-600">Fecha</label>
            <input
              type="date"
              value={fechaFiltro}
              onChange={(e) => setFechaFiltro(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Sorteo */}
          <div>
            <label className="text-sm text-gray-600">Sorteo</label>
            <select
              value={selectedSorteoId}
              onChange={(e) => setSelectedSorteoId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              {sorteos.length === 0 && <option value="">Sin sorteos</option>}
              {sorteos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo_id}){(s.jugadas_hoy ?? 0) > 0 ? ` — ${s.jugadas_hoy} jug.` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Vista */}
          <div>
            <label className="text-sm text-gray-600">Vista</label>
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => setVista('general')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition ${
                  vista === 'general'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                General
              </button>
              <button
                onClick={() => setVista('paquete')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition ${
                  vista === 'paquete'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Por paquete
              </button>
            </div>
          </div>

          {/* Paquete (solo en vista paquete) */}
          <div>
            <label className="text-sm text-gray-600">Paquete</label>
            <select
              value={selectedPaqueteId}
              onChange={(e) => setSelectedPaqueteId(e.target.value)}
              disabled={vista !== 'paquete'}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">{vista === 'paquete' ? 'Seleccionar paquete...' : '—'}</option>
              {paquetes.map((p) => (
                <option key={p.paquete_id} value={p.paquete_id}>
                  {p.paquete_nombre} ({p.local_ids.length} locales)
                </option>
              ))}
            </select>
          </div>

        </div>
      </Card>

      {/* ZONA 2 — Lista consolidada */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">
            Lista consolidada
            {vista === 'paquete' && selectedPaqueteId && auditoria
              ? ` — ${auditoria.paquete_nombre}`
              : ''}
          </h3>
          <div className="flex items-center gap-3">
            {selectedSorteo && (fechaFiltro < fechaLocalYMD() || (fechaFiltro === fechaLocalYMD() && selectedSorteo.estado === 'CERRADO')) && (
              <button
                onClick={descargarPdf}
                disabled={generandoPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generandoPdf ? (
                  <span className="animate-spin text-xs">&#x27F3;</span>
                ) : (
                  <span>&#x2B07;</span>
                )}
                {generandoPdf ? 'Generando\u2026' : 'Descargar PDF'}
              </button>
            )}
            <button
              onClick={enviarWa}
              disabled={rows.length === 0 || enviandoWa}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-300 bg-green-50 text-green-800 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enviandoWa ? 'Enviando…' : 'Enviar por WhatsApp'}
            </button>
            <span className="text-xs text-gray-400">
              Tipo: {selectedSorteo?.tipo_id || '—'} · {rows.length} registro{rows.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {loadingRows ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            {vista === 'paquete' && !selectedPaqueteId
              ? 'Seleccione un paquete para ver la lista filtrada'
              : 'Sin datos para los filtros seleccionados'}
          </p>
        ) : (selectedSorteo?.tipo_id === 'NORMAL' || selectedSorteo?.tipo_id === 'REVENTADO') ? (
          <div className="overflow-x-auto">
            <NumerosGrid
              rows={rows}
              tipo={selectedSorteo?.tipo_id as TipoNumerosGrid}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DynamicListTable
              columns={columnsByTipo(selectedSorteo?.tipo_id || 'NORMAL')}
              rows={formattedRows}
              onSave={async () => {}}
              idField="id"
            />
            {totalesConsolidado && (
              <table className="w-full text-sm mt-1 border-t-2 border-gray-400">
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-2 py-2 w-20">TOTAL</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_vendedor)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_local)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_banca)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}
      </Card>

      {/* ZONA 3 — Bloque de Auditoría */}
      {vista === 'paquete' && selectedPaqueteId && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">
            Auditoría del paquete
          </h3>

          {loadingAuditoria ? (
            <div className="flex justify-center py-6">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !auditoria ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No se pudo cargar la auditoría del paquete
            </p>
          ) : (
            <div className="space-y-3">
              {/* Sección Comisión */}
              <SeccionColapsable titulo="Comisión">
                {auditoria.configs.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin configuración de comisión</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                          <th className="px-3 py-2 text-left font-medium">Tipo</th>
                          <th className="px-3 py-2 text-right font-medium">% Comisión</th>
                          <th className="px-3 py-2 text-right font-medium">Exento</th>
                          <th className="px-3 py-2 text-right font-medium">Máximo</th>
                          <th className="px-3 py-2 text-right font-medium">Límite jugada</th>
                          <th className="px-3 py-2 text-left font-medium">Alcance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {auditoria.configs.map((c, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800">{c.tipo_id}</td>
                            <td className="px-3 py-2 text-right">
                              {c.commission_percentage ? `${c.commission_percentage}%` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {c.commission_exempt_amount ? `₡${monto(c.commission_exempt_amount)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {c.commission_max_amount ? `₡${monto(c.commission_max_amount)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {c.limite_jugada ? `₡${monto(c.limite_jugada)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {c.alcance_limite_jugada || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SeccionColapsable>

              {/* Sección Premios */}
              <SeccionColapsable titulo="Premios">
                {auditoria.premios.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin premios configurados</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                          <th className="px-3 py-2 text-left font-medium">Tipo</th>
                          <th className="px-3 py-2 text-left font-medium">Concepto</th>
                          <th className="px-3 py-2 text-right font-medium">Multiplicador</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {auditoria.premios.map((p, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800">{p.tipo_id}</td>
                            <td className="px-3 py-2 text-gray-700">{p.concepto}</td>
                            <td className="px-3 py-2 text-right font-semibold text-blue-700">
                              x{p.multiplicador}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SeccionColapsable>

              {/* Sección Restricciones */}
              <SeccionColapsable titulo="Restricciones">
                {auditoria.restringidos.length === 0 && auditoria.bloqueados_globales.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin restricciones ni bloqueados</p>
                ) : (
                  <div className="space-y-4">
                    {/* Restringidos por tipo */}
                    {auditoria.restringidos.map((r, i) => (
                      <div key={i} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-semibold text-gray-800">{r.tipo_id}</span>
                          {r.auto_dia && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                              Auto-día
                            </span>
                          )}
                        </div>

                        {/* Números restringidos como chips */}
                        {r.numeros.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {r.numeros.map((num) => (
                              <span
                                key={num}
                                className="inline-block text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-mono"
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600">
                          {r.max_number_sales != null && (
                            <div>
                              <span className="text-gray-400">Máx ventas/número:</span>{' '}
                              <span className="font-medium">{r.max_number_sales.toLocaleString('es-CR')}</span>
                            </div>
                          )}
                          {r.riesgo_pct != null && (
                            <div>
                              <span className="text-gray-400">Riesgo:</span>{' '}
                              <span className="font-medium">{r.riesgo_pct}%</span>
                            </div>
                          )}
                          {r.minimo_permitido != null && (
                            <div>
                              <span className="text-gray-400">Mínimo permitido:</span>{' '}
                              <span className="font-medium">₡{monto(r.minimo_permitido)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Bloqueados globales */}
                    {auditoria.bloqueados_globales.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Bloqueados globales</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                                <th className="px-3 py-2 text-left font-medium">Número</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {auditoria.bloqueados_globales.map((b, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-800">{b.tipo_id}</td>
                                  <td className="px-3 py-2 font-mono text-gray-700">{b.numero}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SeccionColapsable>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
